import { route, string, union } from "react-router-typesafe-routes";
import { zod } from "react-router-typesafe-routes/zod";
import { z } from "zod/v4";

export const rootRoute = route({
  path: "",
  children: {
    signIn: route({
      path: "sign-in",
      searchParams: {
        redirectTo: zod(z.string().optional()),
      },
    }),
    signedOut: route({ path: "signed-out" }),
    references: route({
      path: "references/*",
      searchParams: {
        selectedReference: zod(z.string().uuid().optional()),
      },
      children: {
        list: route({ path: "" }),
        detail: route({ path: ":id" }),
      },
    }),
    // NOTE: This route is used for the pop up window of the reference file image viewer
    referenceFiles: route({
      path: "reference-files/*",
      children: {
        slides: route({
          path: ":referenceId/:referenceFileId",
          searchParams: {
            page: zod(z.number().optional()),
            folio: zod(z.number().optional()),
          },
        }),
      },
    }),
    evidences: route({
      path: "evidences/*",
      searchParams: {
        page: zod(z.number().optional()),
        sortOrder: zod(z.string().optional()),
        sortOrderDirection: zod(z.enum(["asc", "desc"]).optional()),
        referenceId: zod(z.string().optional()),
        userId: zod(z.string().optional()),
        selectedEvidence: zod(z.string().uuid().optional()),
        "tab-evidenceDetail": union(["data", "lineage"]),
      },
      children: {
        list: route({ path: "" }),
        detail: route({ path: ":id" }),
      },
    }),
    entities: route({
      path: "entities/*",
      searchParams: {
        page: zod(z.number().optional()),
        sortOrder: zod(z.string().optional()),
        sortOrderDirection: zod(z.enum(["asc", "desc"]).optional()),
        geoItemId: zod(z.string().optional()),
        idOrName: zod(z.string().optional()),
        categoryId: zod(z.string().optional()),
        referenceId: zod(z.string().optional()),
        excludeDeactivated: zod(z.boolean().optional()),
        gdtfFrom: zod(z.string().optional()),
        gdtfTo: zod(z.string().optional()),
        selectedEntity: zod(z.string().uuid().optional()),
        "tab-entityDetail": union(["data", "changeRequests", "lineage"]),
        view: zod(z.enum(["table", "timeline", "map"]).optional()),
        tlCenterYear: zod(z.number().optional()),
        tlScale: zod(z.number().optional()),
        mapYearMin: zod(z.number().optional()),
        mapYearMax: zod(z.number().optional()),
        mapLat: zod(z.number().optional()),
        mapLng: zod(z.number().optional()),
        mapZoom: zod(z.number().optional()),
      },
      children: {
        list: route({ path: "" }),
        detail: route({ path: ":id" }),
      },
    }),
    relations: route({
      path: "relations/*",
      searchParams: {
        page: zod(z.number().optional()),
        sortOrder: zod(z.string().optional()),
        sortOrderDirection: zod(z.enum(["asc", "desc"]).optional()),
        idOrName: zod(z.string().optional()),
        categoryId: zod(z.string().optional()),
        referenceId: zod(z.string().optional()),
        excludeDeactivated: zod(z.boolean().optional()),
        gdtfFrom: zod(z.string().optional()),
        gdtfTo: zod(z.string().optional()),
        selectedRelation: zod(z.string().uuid().optional()),
        "tab-relationDetail": union(["data", "changeRequests", "lineage"]),
        view: zod(z.enum(["table", "timeline"]).optional()),
        tlCenterYear: zod(z.number().optional()),
        tlScale: zod(z.number().optional()),
      },
      children: {
        list: route({ path: "" }),
        detail: route({ path: ":id" }),
      },
    }),
    changeRequests: route({
      path: "change-requests/*",
      searchParams: {
        page: zod(z.number().optional()),
        sortOrder: zod(z.string().optional()),
        sortOrderDirection: zod(z.enum(["asc", "desc"]).optional()),
        status: zod(z.string().optional()),
        dataImportProjectId: zod(z.string().uuid().optional()),
        changeRequestStatus: union(["open", "rejected", "committed"]),
        hasReviewComments: zod(z.boolean().optional()),
        batchChangeRequestsJobId: zod(z.string().uuid().optional()),
        batchChangeRequestsJobRunId: zod(z.string().uuid().optional()),
        search: zod(z.string().optional()),
        modelType: zod(z.string().optional()),
        categoryId: zod(z.string().uuid().optional()),
        recordComposerDefinitionId: zod(z.string().uuid().optional()),
      },
      children: {
        list: route({
          path: "",
          searchParams: {
            selectedChangeRequest: zod(z.string().uuid().optional()),
          },
        }),
        detail: route({ path: ":id" }),
      },
    }),
    areaInput: route({
      path: "area-input/*",
      children: {
        create: route({ path: "create" }),
        record: route({
          path: ":recordId",
          children: {
            detail: route({
              path: ":geoItemId",
              params: { geoItemId: string() },
              children: {
                edit: route({ path: "edit" }),
              },
            }),
            create: route({
              path: "create",
              searchParams: {
                duplicateGeoItemId: zod(z.string().uuid().optional()),
                h3IndexesFileURLs: zod(z.array(z.string().url()).optional()),
              },
            }),
            createWithOverlayImg: route({
              path: "create-with-overlay",
            }),
          },
        }),
      },
    }),
    dataImportProjects: route({
      path: "data-import-projects/*",
      children: {
        list: route({
          path: "",
          searchParams: {
            "tab-dataImportProjectDetail": union(["dataSource", "evidenceSet", "pipeline"]),
            "tab-dataSourceDetail": union(["info", "jobs", "evidences", "changeRequests"]),
            "tab-evidenceSetDetail": union(["info", "jobs", "evidences", "changeRequests"]),
            "tab-batchChangeRequestsJobDetail": union(["info", "changeRequests"]),
            selectedDataImportProject: zod(z.string().uuid().optional()),
            selectedDataSource: zod(z.string().uuid().optional()),
            selectedEvidenceSet: zod(z.string().uuid().optional()),
            selectedEvidenceEvidenceSets: zod(z.string().uuid().optional()),
            selectedBatchEvidencesJob: zod(z.string().uuid().optional()),
            selectedDataImportPipelineJobRun: zod(z.string().uuid().optional()),
            batchChangeRequestsJobId: zod(z.string().uuid().optional()),
            batchChangeRequestsJobRunId: zod(z.string().uuid().optional()),
            entityComposerId: zod(z.string().uuid().optional()),
            relationComposerId: zod(z.string().uuid().optional()),
            changeRequestStatus: union(["open", "rejected", "committed"]),
            // eslint-disable-next-line camelcase
            BatchChangeRequestsJob_beJob: zod(z.string().uuid().optional()),
            // eslint-disable-next-line camelcase
            BatchChangeRequestsJob_bcrJob: zod(z.string().uuid().optional()),
            // Change request table params (used by embedded ChangeRequestsTable)
            page: zod(z.number().optional()),
            sortOrder: zod(z.string().optional()),
            sortOrderDirection: zod(z.enum(["asc", "desc"]).optional()),
            search: zod(z.string().optional()),
            hasReviewComments: zod(z.boolean().optional()),
            selectedChangeRequest: zod(z.string().uuid().optional()),
            modelType: zod(z.string().optional()),
            categoryId: zod(z.string().uuid().optional()),
            recordComposerDefinitionId: zod(z.string().uuid().optional()),
          },
        }),
        detail: route({
          path: ":id",
          searchParams: {
            "tab-dataImportProjectDetail": union(["dataSource", "evidenceSet", "pipeline"]),
            "tab-dataSourceDetail": union(["info", "jobs", "evidences", "changeRequests"]),
            "tab-evidenceSetDetail": union(["info", "jobs", "evidences", "changeRequests"]),
            "tab-batchChangeRequestsJobDetail": union(["info", "changeRequests"]),
            selectedDataSource: zod(z.string().uuid().optional()),
            selectedEvidenceSet: zod(z.string().uuid().optional()),
            selectedEvidenceEvidenceSets: zod(z.string().uuid().optional()),
            selectedBatchEvidencesJob: zod(z.string().uuid().optional()),
            // eslint-disable-next-line camelcase
            BatchChangeRequestsJob_beJob: zod(z.string().uuid().optional()),
            // eslint-disable-next-line camelcase
            BatchChangeRequestsJob_bcrJob: zod(z.string().uuid().optional()),
            batchChangeRequestsJobId: zod(z.string().uuid().optional()),
            batchChangeRequestsJobRunId: zod(z.string().uuid().optional()),
            entityComposerId: zod(z.string().uuid().optional()),
            relationComposerId: zod(z.string().uuid().optional()),
            changeRequestStatus: union(["open", "rejected", "committed"]),
            reload: zod(z.boolean().optional()),
            // Change request table params (used by embedded ChangeRequestsTable)
            page: zod(z.number().optional()),
            sortOrder: zod(z.string().optional()),
            sortOrderDirection: zod(z.enum(["asc", "desc"]).optional()),
            search: zod(z.string().optional()),
            hasReviewComments: zod(z.boolean().optional()),
            selectedChangeRequest: zod(z.string().uuid().optional()),
            modelType: zod(z.string().optional()),
            categoryId: zod(z.string().uuid().optional()),
            recordComposerDefinitionId: zod(z.string().uuid().optional()),
          },
          children: {
            batchEvidencesJob: route({ path: "batch-evidences-jobs/:jobId" }),
            batchChangeRequestsJob: route({
              path: "batch-change-request-jobs/:jobId",
            }),
            dataImportPipelineJobs: route({
              path: "data-import-pipeline-jobs",
              children: {
                list: route({
                  path: "",
                  children: {
                    dataImportPipelineJobRun: route({ path: "runs/:jobRunId" }),
                  },
                }),
              },
            }),
            dataSourceDesigner: route({ path: "data-sources/designer" }),
          },
        }),
      },
    }),
    dataSourceTemplates: route({
      path: "data-source-templates/*",
      searchParams: {
        selectedDataSourceTemplate: zod(z.string().uuid().optional()),
      },
      children: {
        list: route({ path: "" }),
        detail: route({ path: ":id" }),
      },
    }),
    admin: route({
      path: "admin",
      children: {
        tenants: route({
          path: "tenants/*",
          searchParams: {
            page: zod(z.number().optional()),
            selectedTenant: zod(z.string().uuid().optional()),
            userIds: zod(z.array(z.string().uuid()).optional()),
          },
          children: {
            list: route({ path: "" }),
            detail: route({ path: ":id" }),
          },
        }),
        categories: route({
          path: "categories/*",
          searchParams: {
            "tab-categoriesPage": union([
              "categories",
              "entityCategoriesTree",
              "relationCategoriesTree",
              "entityCategoriesTable",
              "relationCategoriesTable",
            ]),
            page: zod(z.number().optional()),
            selectedCategory: zod(z.string().uuid().optional()),
          },
          children: {
            list: route({ path: "" }),
            detail: route({ path: ":id" }),
          },
        }),
        recordMetadataSchema: route({
          path: "record-metadata-schema/*",
          searchParams: {
            selectedRecordMetadataSchema: zod(z.string().uuid().optional()),
          },
          children: {
            list: route({ path: "" }),
            detail: route({ path: ":id" }),
          },
        }),
        relationTypes: route({
          path: "relation-types/*",
          searchParams: {
            selectedRelationType: zod(z.string().uuid().optional()),
          },
          children: {
            list: route({ path: "" }),
            detail: route({ path: ":id" }),
          },
        }),
        products: route({
          path: "products/*",
          searchParams: {
            selectedProduct: zod(z.string().uuid().optional()),
          },
          children: {
            list: route({ path: "" }),
            detail: route({ path: ":id" }),
          },
        }),
        users: route({
          path: "users/*",
          searchParams: {
            selectedUser: zod(z.string().uuid().optional()),
          },
          children: {
            list: route({ path: "" }),
            detail: route({ path: ":id" }),
            bulkUpload: route({ path: "bulk-upload" }),
          },
        }),
        groups: route({
          path: "groups/*",
          searchParams: {
            selectedGroup: zod(z.string().uuid().optional()),
          },
          children: {
            list: route({ path: "" }),
            detail: route({ path: ":id" }),
            new: route({ path: "new" }),
            edit: route({ path: ":id/edit" }),
          },
        }),
      },
    }),
  },
});
